import { createClient, type SupabaseClient } from '@supabase/supabase-js';

export const SUPABASE_AUTH_STORAGE_KEY = 'trade-proof-supabase-auth';
export const SUPABASE_AUTH_USER_STORAGE_KEY = `${SUPABASE_AUTH_STORAGE_KEY}-user`;

const STORAGE_KEY = SUPABASE_AUTH_STORAGE_KEY;
export const DIRECT_SUPABASE_SIGN_UP_FORBIDDEN_MESSAGE = 'Direct Supabase signUp is forbidden. Use /api/auth/signup-identifier';

let supabaseClient: SupabaseClient | null = null;

const clean = (value: string | undefined) => {
  if (!value) return '';
  let next = value.trim();
  if ((next.startsWith('"') && next.endsWith('"')) || (next.startsWith("'") && next.endsWith("'"))) {
    next = next.slice(1, -1);
  }
  return next;
};

const normalizeSupabaseUrl = (value: string | undefined) => {
  const cleaned = clean(value);
  if (!cleaned) return '';

  return cleaned
    .replace(/\/auth\/v1\/callback\/?$/i, '')
    .replace(/\/auth\/v1\/?$/i, '')
    .replace(/\/+$/, '');
};

const resolvePublicKey = () => {
  return clean(import.meta.env.VITE_SUPABASE_ANON_KEY) || clean(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY);
};

export function isSupabaseConfigured() {
  return Boolean(normalizeSupabaseUrl(import.meta.env.VITE_SUPABASE_URL) && resolvePublicKey());
}

function forbidDirectBrowserSignup(client: SupabaseClient) {
  const authClient = client.auth as typeof client.auth & {
    __tradeProofSignUpGuardInstalled?: boolean;
    signUp?: (...args: unknown[]) => Promise<never>;
  };

  if (authClient.__tradeProofSignUpGuardInstalled) {
    return client;
  }

  const forbiddenSignUp = async () => {
    throw new Error(DIRECT_SUPABASE_SIGN_UP_FORBIDDEN_MESSAGE);
  };

  try {
    Object.defineProperty(authClient, 'signUp', {
      configurable: true,
      enumerable: true,
      writable: false,
      value: forbiddenSignUp,
    });
  } catch {
    authClient.signUp = forbiddenSignUp;
  }

  Object.defineProperty(authClient, '__tradeProofSignUpGuardInstalled', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: true,
  });

  return client;
}

export function getSupabaseClient(): SupabaseClient | null {
  if (supabaseClient) return supabaseClient;

  const url = normalizeSupabaseUrl(import.meta.env.VITE_SUPABASE_URL);
  const anonKey = resolvePublicKey();

  if (!url || !anonKey) return null;

  supabaseClient = createClient(url, anonKey, {
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: true,
      persistSession: true,
      storageKey: STORAGE_KEY,
    },
  });

  forbidDirectBrowserSignup(supabaseClient);

  return supabaseClient;
}
